import { useEffect, useState } from 'react';

// The presentation stage is shrunk to fit the slide via `transform: scale()` —
// PowerPointView scales the `.ppt-stage` element, PresentationView scales the
// immediate `<div>` under `#presentationView`. Our modals portal to
// document.body, so they escape that transform and render at 1:1, looking
// oversized next to the scaled-down slide content. We read the live stage scale
// and apply it to the modal so it visually matches.
//
// The catch is legibility: some embeds (e.g. Gamma) shrink the stage so far that
// matching it 1:1 makes the modal text unreadable, while others (e.g. Canva)
// barely shrink it, so *not* scaling leaves the modal looking oversized. A blunt
// "don't scale inside iframes" rule fixed the first at the cost of the second.
// Instead we clamp the applied scale to a legible floor: the modal still tracks
// the stage downward to stay proportional, but never past the point where its
// own text becomes too small to read. Anything at/above the floor is honoured
// as-is, so lightly-scaled hosts shrink only as much as they actually need to.
const MIN_LEGIBLE_SCALE = 0.75;

const clampScale = (scale: number): number =>
  Math.min(1, Math.max(scale, MIN_LEGIBLE_SCALE));

const readStageScale = (): number => {
  if (typeof document === 'undefined') {
    return 1;
  }

  const stage =
    document.querySelector<HTMLElement>('.ppt-stage') ||
    document.querySelector<HTMLElement>('#presentationView > div');

  if (!stage) {
    return 1;
  }

  const { transform } = getComputedStyle(stage);
  if (!transform || transform === 'none') {
    return 1;
  }

  try {
    return clampScale(new DOMMatrixReadOnly(transform).a || 1);
  } catch {
    return 1;
  }
};

const usePresentationScale = (isOpen: boolean): number => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const update = () => setScale(readStageScale());
    update();

    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isOpen]);

  return scale;
};

export default usePresentationScale;
